import { useState } from "react";
import { toast } from "sonner";
import { Mail, Pencil, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BrandButton } from "@/components/PageHeader";
import { SpecialReceiptView } from "@/components/special-receipts/SpecialReceiptView";
import { api, errMsg, fmtDate } from "@/lib/api";

const EmailBox = ({ r, onSent }) => {
  const [to, setTo] = useState(r.email || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { const res = await api.post(`/special-receipts/${r.id}/email`, { email: to }); toast.success(`Receipt emailed to ${res.data.emailed_to}`); onSent(res.data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="Recipient email" className="h-10" data-testid="special-email-to-input" />
      <BrandButton variant="outline" onClick={send} disabled={busy} data-testid="special-email-button"><Mail className="w-4 h-4" />{busy ? "Sending…" : "Email receipt"}</BrandButton>
    </div>
  );
};

const StaffNotes = ({ r }) => (r.note || r.emailed_at || r.edit_history?.length) ? (
  <div className="rounded-md bg-[#F8F9FA] border p-3 text-xs space-y-1" data-testid="special-staff-notes">
    <div className="font-bold uppercase tracking-wider text-[10px] text-gray-500">Staff only · not on the receipt</div>
    {r.note && <div data-testid="special-staff-note"><span className="text-gray-500">Note:</span> {r.note}</div>}
    {r.emailed_at && <div><span className="text-gray-500">Last emailed:</span> {r.emailed_to} · {fmtDate(r.emailed_at)}</div>}
    {r.edit_history?.map((h, i) => <div key={i}><span className="text-gray-500">Edited {fmtDate(h.at)} by {h.by || "staff"}:</span> {h.reason}</div>)}
  </div>
) : null;

export const ReceiptDialog = ({ r, canEdit, onClose, onEdit, onChanged }) => (
  <Dialog open={!!r} onOpenChange={() => onClose()}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="special-receipt-dialog">
      <DialogHeader><DialogTitle className="sr-only">Special receipt</DialogTitle></DialogHeader>
      {r && <>
        <SpecialReceiptView r={r} />
        <StaffNotes r={r} />
        {!r.voided && <EmailBox key={r.id} r={r} onSent={onChanged} />}
        <div className="flex flex-wrap gap-2">
          <BrandButton variant="dark" onClick={() => window.print()} data-testid="special-print-button"><Printer className="w-4 h-4" />Print receipt</BrandButton>
          {canEdit && !r.voided && <BrandButton variant="outline" onClick={() => onEdit(r)} data-testid="special-edit-button"><Pencil className="w-4 h-4" />Edit</BrandButton>}
        </div>
      </>}
    </DialogContent>
  </Dialog>
);
